import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown, Menu, X, MessageCircle } from "lucide-react";
import { LeafMark } from "./LeafMark";
import { SITE } from "../lib/site";
import { useLang } from "../i18n/LanguageContext";

const Wordmark = ({ onClick }) => (
    <Link
        to="/"
        onClick={onClick}
        data-testid="header-logo"
        className="flex items-center gap-2.5 group"
        aria-label="UniversHeal — Home"
    >
        <span className="text-forest group-hover:text-pine transition-colors">
            <LeafMark className="w-8 h-8" />
        </span>
        <span className="leading-none">
            <span className="block text-[19px] font-extrabold tracking-[0.18em] text-forest">
                UNIVERS<span className="text-pine">HEAL</span>
            </span>
            <span className="hidden lg:block font-serif italic text-[11px] text-moss mt-1.5 tracking-normal">
                {SITE.tagline}
            </span>
        </span>
    </Link>
);

export const Header = () => {
    const { lang, setLang, t, langs, site: siteData } = useLang();
    const { NAV_LINKS, PATIENT_SERVICE_LINKS, LEGAL_LINKS } = siteData;
    const current = langs.find((l) => l.code === lang) || langs[0];
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setMenuOpen(false);
        setLangOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "";
        window.__lenis && (menuOpen ? window.__lenis.stop() : window.__lenis.start());
        return () => {
            document.body.style.overflow = "";
        };
    }, [menuOpen]);

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                    scrolled
                        ? "bg-ivory/90 backdrop-blur-xl border-b border-pine/10 shadow-[0_1px_20px_rgba(6,60,53,0.05)]"
                        : "bg-ivory/40 backdrop-blur-sm"
                }`}
            >
                <div className="container-site flex items-center justify-between h-[80px]">
                    <Wordmark />

                    <nav
                        aria-label="Primary"
                        className="hidden xl:flex items-center gap-5 2xl:gap-7"
                        data-testid="header-nav"
                    >
                        {NAV_LINKS.map((link) => (
                            <NavLink
                                key={link.path}
                                to={link.path}
                                end={link.path === "/"}
                                data-testid={`nav-link-${link.path === "/" ? "home" : link.path.slice(1).replace(/\//g, "-")}`}
                                className={({ isActive }) =>
                                    `relative whitespace-nowrap text-[13px] font-semibold tracking-wide transition-colors duration-200 hover:text-forest ${
                                        isActive ? "text-forest" : "text-moss"
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        {link.label}
                                        <span
                                            className={`absolute -bottom-1.5 left-0 h-px bg-forest transition-all duration-300 ${
                                                isActive ? "w-full" : "w-0"
                                            }`}
                                        />
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3 sm:gap-5">
                        {/* Display-only language selector — placeholder for future languages */}
                        <div className="relative hidden sm:block">
                            <button
                                type="button"
                                data-testid="language-toggle"
                                aria-haspopup="true"
                                aria-expanded={langOpen}
                                aria-label={t("common.selectLanguage", "Select language")}
                                onClick={() => setLangOpen((v) => !v)}
                                className="flex items-center gap-1 text-[13px] font-bold tracking-widest text-forest/80 hover:text-forest transition-colors px-2 py-1.5"
                            >
                                {current.code.toUpperCase()}
                                <ChevronDown
                                    className={`w-3.5 h-3.5 transition-transform duration-300 ${
                                        langOpen ? "rotate-180" : ""
                                    }`}
                                />
                            </button>
                            <AnimatePresence>
                                {langOpen && (
                                    <>
                                        <div
                                            className="fixed inset-0 z-40"
                                            onClick={() => setLangOpen(false)}
                                            aria-hidden="true"
                                        />
                                        <div
                                            data-testid="language-menu"
                                            className="absolute right-0 top-full mt-2 z-50 w-52 bg-white rounded-xl shadow-lg shadow-forest/10 border border-pine/10 p-2"
                                        >
                                            {langs.map((l) => (
                                                <button
                                                    key={l.code}
                                                    type="button"
                                                    data-testid={`language-option-${l.code}`}
                                                    onClick={() => {
                                                        setLang(l.code);
                                                        setLangOpen(false);
                                                    }}
                                                    className={`w-full px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-colors ${
                                                        l.code === lang
                                                            ? "font-bold text-forest bg-sage/60"
                                                            : "text-moss hover:bg-sage/40 hover:text-forest"
                                                    }`}
                                                >
                                                    <span>{l.label}</span>
                                                    {l.code === lang && (
                                                        <span className="text-[10px] uppercase tracking-widest text-natural">
                                                            ●
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </AnimatePresence>
                        </div>

                        <Link
                            to="/get-a-medical-opinion"
                            data-testid="header-cta"
                            className="hidden md:inline-flex btn-primary !px-5 !py-2.5 group"
                        >
                            {t("common.get_medical_opinion", "Get a Medical Opinion")}
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>

                        <button
                            type="button"
                            data-testid="mobile-menu-toggle"
                            aria-label={menuOpen ? "Close menu" : "Open menu"}
                            aria-expanded={menuOpen}
                            onClick={() => setMenuOpen((v) => !v)}
                            className="xl:hidden flex items-center justify-center w-11 h-11 rounded-xl border border-pine/20 text-forest hover:bg-forest hover:text-ivory transition-all duration-300"
                        >
                            {menuOpen ? (
                                <X className="w-5 h-5" />
                            ) : (
                                <Menu className="w-5 h-5" />
                            )}
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile drawer */}
            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0, y: -16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -16 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="fixed inset-0 z-40 xl:hidden bg-forest text-ivory overflow-y-auto"
                    >
                        <div className="container-site pt-[104px] pb-16 min-h-full flex flex-col">
                            <nav aria-label="Mobile" className="flex-1">
                                {NAV_LINKS.map((link, i) => (
                                    <motion.div
                                        key={link.path}
                                        initial={{ opacity: 0, x: -18 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{
                                            delay: 0.08 + i * 0.05,
                                            duration: 0.5,
                                            ease: [0.22, 1, 0.36, 1],
                                        }}
                                    >
                                        <NavLink
                                            to={link.path}
                                            end={link.path === "/"}
                                            data-testid={`mobile-nav-link-${link.path === "/" ? "home" : link.path.slice(1).replace(/\//g, "-")}`}
                                            className={({ isActive }) =>
                                                `block font-serif text-[2rem] leading-snug py-2 border-b border-ivory/10 ${
                                                    isActive
                                                        ? "italic text-natural"
                                                        : "text-ivory"
                                                }`
                                            }
                                        >
                                            {link.label}
                                        </NavLink>
                                    </motion.div>
                                ))}
                            </nav>

                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.45, duration: 0.5 }}
                                className="mt-8 space-y-4"
                            >
                                <Link
                                    to="/get-a-medical-opinion"
                                    data-testid="mobile-menu-cta"
                                    className="btn-ivory w-full group"
                                >
                                    {t("common.get_medical_opinion", "Get a Medical Opinion")}
                                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                                </Link>
                                <a
                                    href={SITE.whatsappUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="mobile-menu-whatsapp"
                                    className="btn-outline-light w-full"
                                >
                                    <MessageCircle className="w-4 h-4" />
                                    {t("common.chat_whatsapp", "Chat on WhatsApp")}
                                </a>

                                <div
                                    className="mt-3 pt-4 border-t border-pine/10"
                                    data-testid="mobile-language-selector"
                                >
                                    <p className="eyebrow text-moss text-[10px] mb-2.5">{t("common.language", "Language")}</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        {langs.map((l) => (
                                            <button
                                                key={l.code}
                                                type="button"
                                                data-testid={`mobile-language-option-${l.code}`}
                                                onClick={() => setLang(l.code)}
                                                className={`rounded-lg border px-3 py-2 text-sm transition-colors ${
                                                    l.code === lang
                                                        ? "border-forest bg-sage/60 font-bold text-forest"
                                                        : "border-pine/15 text-moss hover:border-forest/40 hover:text-forest"
                                                }`}
                                            >
                                                {l.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="pt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-ivory/50">
                                    {PATIENT_SERVICE_LINKS.slice(0, 3)
                                        .concat(LEGAL_LINKS)
                                        .map((l) => (
                                            <Link
                                                key={l.path + l.label}
                                                to={l.path}
                                                className="hover:text-ivory transition-colors"
                                            >
                                                {l.label}
                                            </Link>
                                        ))}
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
